import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Lock } from 'lucide-react';
import { SheetBlock, SheetSection } from '../settings/sheet';
import type { CancelGroup, ChannelRow, DayRow, HomeData, HourRow, PaymentRow, PeriodKey, ProductRow, TodaySoFar } from './homeApi';
import {
  fmtDM, fmtEur, fmtEur0, fmtEurSigned, fmtNum, fmtPct, fmtWeekday, fmtWeekdayShort, hh, hhmm, ymdToDate,
} from './format';
import {
  PLATFORM_NAMES, activeHours, dayStats, groupChannels, mergeCounts, paymentLines, paymentStack, peakWindow, platformTotals, prevFromDelta,
  staffLines, sum,
} from './insights';
import { Bars, BarDatum } from './charts/Bars';
import { HourLine, buildCumulative } from './charts/HourLine';
import { StackedBar } from './charts/StackedBar';
import { Fig, FigLines, Legend, MetricSeg, Note, rich } from './ui';

/**
 * Οι ενότητες της Αρχικής (σχέδιο A): αριστερά τίτλος, μία γραμμή, το νούμερο και το «συμπέρασμα» —
 * δεξιά το γράφημα/πίνακας. Όλα τα νούμερα από το GET …/home· τα κείμενα από το home.json.
 */

export const HOME_TOP_PRODUCTS = 5;

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** «από χθες» / «από προχθές» / «από τις προηγ. 7 ημέρες» … (για Δ) */
export function compareSuffix(t: TFn, key: PeriodKey): string {
  switch (key) {
    case 'today': return t('home:cmp.yesterday');
    case 'yesterday': return t('home:cmp.dayBefore');
    case '7d': return t('home:cmp.prev7');
    case '30d': return t('home:cmp.prev30');
    default: return t('home:cmp.prevPeriod');
  }
}

/** «χθες 9» / «προχθές 9» / «πριν 9» (σύγκριση πλήθους) */
export function compareShort(t: TFn, key: PeriodKey, count: number): string {
  if (key === 'today') return t('home:cmp.shortYesterday', { count });
  if (key === 'yesterday') return t('home:cmp.shortDayBefore', { count });
  return t('home:cmp.shortPrev', { count });
}

/** Επικεφαλίδα της στήλης «Δ» των προϊόντων. */
export function deltaHeader(t: TFn, key: PeriodKey): string {
  if (key === 'today') return t('home:cmp.deltaYesterday');
  if (key === 'yesterday') return t('home:cmp.deltaDayBefore');
  return t('home:cmp.deltaPrev');
}

const ShareCell: React.FC<{ share: number; max: number }> = ({ share, max }) => (
  <span className="xh-share">
    <span className="tr" aria-hidden="true"><i style={{ width: `${Math.min(100, max > 0 ? (share / max) * 100 : 0).toFixed(1)}%` }} /></span>
    <span>{fmtPct(share)}</span>
  </span>
);

const MoreLink: React.FC<{ label: string; onClick?: () => void; locked?: boolean; lockedTitle?: string; testId?: string }> = ({ label, onClick, locked, lockedTitle, testId }) => (
  <p className="xh-more">
    <button type="button" className="xh-link" onClick={onClick} title={locked ? lockedTitle : undefined} data-testid={testId} data-locked={locked ? 'true' : undefined}>
      {label}
      {locked ? <Lock className="xh-lock" aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
    </button>
  </p>
);

/* ───────── Σήμερα ως τώρα ───────── */

export const TodaySection: React.FC<{ tsf: TodaySoFar; yesterdayYmd: string | null; until: string }> = ({ tsf, yesterdayYmd, until }) => {
  const { t, i18n } = useTranslation(['home']);
  const hasYesterday = tsf.yesterday !== null;
  const model = useMemo(() => {
    const m = buildCumulative(tsf.hourly, tsf.until);
    // Χωρίς χθεσινή περίοδο (νέο κατάστημα): μόνο η σημερινή γραμμή.
    return m && !hasYesterday ? { ...m, yesterday: [], yesterdayEnd: 0 } : m;
  }, [tsf, hasYesterday]);
  const diff = tsf.today.revenue - (tsf.yesterday?.revenue ?? 0);
  const yTotal = model ? model.yesterdayEnd : (tsf.yesterday?.revenue ?? 0);
  const yDay = yesterdayYmd ? fmtWeekday(yesterdayYmd, i18n.language) : '';
  return (
    <SheetSection
      id="home-today"
      title={t('home:today.title')}
      indexLabel={t('home:today.index')}
      hint={t('home:today.hint')}
      testId="section-today"
      aside={hasYesterday
        ? <Fig value={fmtEurSigned(diff)} sub={t('home:today.figSub', { time: until })} testId="today-fig" />
        : <Fig value="—" sub={t('home:today.figSubNoYesterday')} testId="today-fig" />}
    >
      {model ? (
        <>
          <HourLine
            model={model}
            height={186}
            ariaLabel={t('home:today.chart')}
            axisLabel={(v, top) => (v === 0 ? '0' : top ? fmtEur0(v) : fmtNum(v))}
            hourLabel={hhmm}
            endLabel={fmtEur0(model.todayEnd)}
            nowLabel={t('home:today.now', { time: tsf.until })}
            tip={(row, next) => (hasYesterday
              ? t('home:today.tip', { amount: fmtEur(row.today ?? 0), time: hhmm(next ? next.hour : row.hour + 1), yesterday: fmtEur(row.yesterday) })
              : t('home:today.tipNoYesterday', { amount: fmtEur(row.today ?? 0), time: hhmm(next ? next.hour : row.hour + 1) }))}
          />
          <Legend
            items={[
              { key: 'today', label: t('home:today.legendToday'), swatch: 'bg1', line: true },
              ...(hasYesterday ? [{
                key: 'yesterday',
                label: yDay ? t('home:today.legendYesterday', { day: yDay, amount: fmtEur(yTotal) }) : t('home:today.legendYesterdayPlain', { amount: fmtEur(yTotal) }),
                swatch: 'bgn',
                line: true,
              }] : []),
            ]}
          />
        </>
      ) : (
        <p className="xh-empty">{t('home:today.empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Ανά ημέρα ───────── */

export const DaysSection: React.FC<{ rows: DayRow[]; periodKey: PeriodKey; todayYmd: string }> = ({ rows, periodKey, todayYmd }) => {
  const { t, i18n } = useTranslation(['home']);
  const [metric, setMetric] = useState<'revenue' | 'orders'>('revenue');
  const lang = i18n.language;
  const selectedLast = periodKey === 'today' || periodKey === 'yesterday';
  const stats = dayStats(rows, metric, selectedLast);
  const fmt = metric === 'revenue' ? fmtEur0 : fmtNum;
  const fmtFull = metric === 'revenue' ? fmtEur : fmtNum;
  const n = rows.length;
  const step = n > 10 ? Math.ceil(n / 8) : 1;

  // Μία στήλη ανά περίοδο ταμείου (σήμερα/χθες) ή ημέρα: «Σήμερα»/«Χθες» μόνο η τελευταία — μια κλειστή
  // περίοδος με σημερινή ημερομηνία (Ζ μετά τα μεσάνυχτα) δεν είναι «σήμερα».
  const dayLabel = (r: DayRow, i: number): string => {
    const last = i === n - 1;
    if (last && periodKey === 'today') return t('home:days.today');
    if (last && periodKey === 'yesterday') return t('home:days.yesterday');
    if (last && r.date === todayYmd) return t('home:days.today');
    const d = ymdToDate(r.date);
    return Number.isNaN(d.getTime()) ? r.label : `${fmtWeekdayShort(r.date, lang)} ${d.getUTCDate()}`;
  };

  const data: BarDatum[] = rows.map((r, i) => {
    const last = i === n - 1;
    const strong = last && (selectedLast || r.date === todayYmd);
    const showLabel = n <= 10 || i % step === 0 || last;
    return {
      key: r.date || String(i),
      value: r[metric],
      label: showLabel ? dayLabel(r, i) : '',
      sub: n <= 10 ? (metric === 'revenue' ? t('home:days.ordersShort', { n: fmtNum(r.orders) }) : fmtEur0(r.revenue)) : undefined,
      strong,
      bold: strong,
      showValue: strong || (stats !== null && i === stats.best),
      tip: `${fmtWeekday(r.date, lang)} ${fmtDM(r.date)} · ${fmtEur(r.revenue)} · ${t('home:days.ordersCount', { count: r.orders, n: fmtNum(r.orders) })}`,
    };
  });

  let note: React.ReactNode = null;
  if (stats && n > 1) {
    const best = rows[stats.best];
    const parts = [t('home:days.best', { day: `${fmtWeekday(best.date, lang)} ${fmtDM(best.date)}`, value: fmtFull(best[metric]) })];
    if (selectedLast && stats.lastVsAvg !== null) {
      const who = periodKey === 'today' ? 'today' : 'yesterday';
      const pct = fmtPct(Math.abs(stats.lastVsAvg));
      parts.push(Math.abs(stats.lastVsAvg) < 0.5
        ? t(`home:days.${who}AtAvg`)
        : t(stats.lastVsAvg > 0 ? `home:days.${who}Above` : `home:days.${who}Below`, { pct }));
    }
    note = rich(parts.join(' '));
  }

  return (
    <SheetSection
      id="home-days"
      title={t('home:days.title')}
      hint={selectedLast ? t('home:days.hintLast7') : t('home:days.hintPeriod')}
      testId="section-days"
      aside={(
        <>
          <Fig value={stats ? fmt(stats.avg) : '—'} sub={t('home:days.figSub')} testId="days-fig" />
          {note && <Note testId="days-note">{note}</Note>}
          <MetricSeg
            value={metric}
            onChange={setMetric}
            label={t('home:days.metric')}
            options={[{ value: 'revenue', label: t('home:metric.revenue') }, { value: 'orders', label: t('home:metric.orders') }]}
          />
        </>
      )}
    >
      {n > 0 ? (
        <Bars
          data={data}
          height={200}
          format={fmt}
          maxBarWidth={24}
          barRatio={0.42}
          reference={stats && n > 1 ? { value: stats.avg, label: t('home:days.avg', { value: fmt(stats.avg) }) } : null}
          ariaLabel={t('home:days.chart')}
          testId="days-chart"
        />
      ) : (
        <p className="xh-empty">{t('home:empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Ανά ώρα ───────── */

export const HoursSection: React.FC<{ byHour: HourRow[] }> = ({ byHour }) => {
  const { t } = useTranslation(['home']);
  const [metric, setMetric] = useState<'orders' | 'revenue'>('orders');
  const rows = useMemo(() => activeHours(byHour), [byHour]);
  const peak = peakWindow(rows, metric);
  const inPeak = (i: number) => !!peak && i >= peak.start && i <= peak.end;
  const fmt = metric === 'orders' ? fmtNum : fmtEur0;
  const data: BarDatum[] = rows.map((r, i) => ({
    key: String(r.hour),
    value: r[metric],
    label: r.hour % 2 === 0 ? hh(r.hour) : '',
    strong: inPeak(i),
    showValue: inPeak(i),
    tip: `${hhmm(r.hour)}–${hhmm(r.hour + 1)} · ${t('home:days.ordersCount', { count: r.orders, n: fmtNum(r.orders) })} · ${fmtEur(r.revenue)}`,
  }));
  const figValue = peak ? `${hhmm(rows[peak.start].hour)}–${hhmm(rows[peak.end].hour + 1)}` : '—';
  const figSub = peak
    ? t('home:hours.figSub', {
      value: metric === 'orders' ? t('home:days.ordersCount', { count: peak.value, n: fmtNum(peak.value) }) : fmtEur(peak.value),
      pct: `${fmtNum(peak.share)}%`,
    })
    : t('home:empty');
  return (
    <SheetSection
      id="home-hours"
      title={t('home:hours.title')}
      hint={t('home:hours.hint')}
      testId="section-hours"
      aside={(
        <>
          <Fig value={figValue} sub={figSub} testId="hours-fig" />
          <MetricSeg
            value={metric}
            onChange={setMetric}
            label={t('home:hours.metric')}
            options={[{ value: 'orders', label: t('home:metric.orders') }, { value: 'revenue', label: t('home:metric.revenue') }]}
          />
        </>
      )}
    >
      {rows.length > 0 ? (
        <>
          <Bars data={data} height={178} format={fmt} maxBarWidth={20} barRatio={0.56} ariaLabel={t('home:hours.chart')} testId="hours-chart" />
          <Legend items={[{ key: 'peak', label: t('home:hours.peak'), swatch: 'bg1' }, { key: 'rest', label: t('home:hours.rest'), swatch: 'bg3' }]} />
        </>
      ) : (
        <p className="xh-empty">{t('home:empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Κανάλια ───────── */

export const ChannelsSection: React.FC<{ channels: ChannelRow[] }> = ({ channels }) => {
  const { t } = useTranslation(['home']);
  const groups = groupChannels(channels);
  const groupLabel = (key: string, server: string): string => {
    if (key === 'takeaway') return t('home:channels.takeaway');
    if (key === 'delivery') return t('home:channels.delivery');
    if (key === 'dine_in') return t('home:channels.dineIn');
    return server || key;
  };
  const childLabel = (key: string, server: string): string => (key === 'delivery' ? t('home:channels.own') : PLATFORM_NAMES[key] || server || key);
  const biggest = groups.reduce<typeof groups[number] | null>((b, g) => (!b || g.revenue > b.revenue ? g : b), null);
  const basket = groups.filter(g => g.orders > 0).reduce<typeof groups[number] | null>((b, g) => (!b || g.avg > b.avg ? g : b), null);
  return (
    <SheetSection
      id="home-channels"
      title={t('home:channels.title')}
      hint={t('home:channels.hint')}
      testId="section-channels"
      aside={biggest ? (
        <>
          <Fig value={fmtPct(biggest.share)} sub={t('home:channels.figSub', { name: groupLabel(biggest.key, biggest.label) })} testId="channels-fig" />
          {basket && groups.length > 1 && (
            <Note testId="channels-note">{rich(t('home:channels.basket', { name: groupLabel(basket.key, basket.label), amount: fmtEur(basket.avg) }))}</Note>
          )}
        </>
      ) : undefined}
    >
      {groups.length > 0 ? (
        <>
          <StackedBar parts={groups.map(g => ({ key: g.key, label: groupLabel(g.key, g.label), value: Math.max(0, g.revenue), tone: g.tone, tip: `${groupLabel(g.key, g.label)} · ${fmtEur(g.revenue)} · ${fmtPct(g.share)}` }))} testId="channels-stack" />
          <table className="xh-tbl" data-testid="channels-table">
            <thead>
              <tr>
                <th>{t('home:channels.colChannel')}</th>
                <th>{t('home:channels.colOrders')}</th>
                <th>{t('home:channels.colRevenue')}</th>
                <th className="xh-opt">{t('home:channels.colBasket')}</th>
                <th>{t('home:channels.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <React.Fragment key={g.key}>
                  <tr>
                    <td><span className="xh-nm"><span className={`xh-sw bg${g.tone}`} aria-hidden="true" /><span className="xh-trunc">{groupLabel(g.key, g.label)}</span></span></td>
                    <td>{fmtNum(g.orders)}</td>
                    <td className="strong">{fmtEur(g.revenue)}</td>
                    <td className="xh-opt">{g.orders > 0 ? fmtEur(g.avg) : '—'}</td>
                    <td>{fmtPct(g.share)}</td>
                  </tr>
                  {g.children.map(c => (
                    <tr key={`${g.key}-${c.key}`} className="child">
                      <td><span className="xh-trunc">{childLabel(c.key, c.label)}</span></td>
                      <td>{fmtNum(c.orders)}</td>
                      <td>{fmtEur(c.revenue)}</td>
                      <td className="xh-opt">{c.orders > 0 ? fmtEur(c.revenue / c.orders) : '—'}</td>
                      <td />
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="xh-empty">{t('home:channels.empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Πληρωμές ───────── */

export const PaymentsSection: React.FC<{ payments: PaymentRow[] }> = ({ payments }) => {
  const { t } = useTranslation(['home']);
  const lines = paymentLines(payments);
  const label = (key: string, server: string): string => {
    const known = ['cash', 'card', 'online', 'iris', 'credit', 'deposit'];
    return known.includes(key) ? t(`home:payments.labels.${key}`) : (server || t('home:payments.labels.other'));
  };
  const cash = lines.find(l => l.key === 'cash');
  const top = cash || lines.reduce<typeof lines[number] | null>((b, l) => (!b || l.share > b.share ? l : b), null);
  const credit = lines.find(l => l.key === 'credit' && l.amount > 0);
  return (
    <SheetSection
      id="home-payments"
      title={t('home:payments.title')}
      hint={t('home:payments.hint')}
      testId="section-payments"
      aside={top ? (
        <>
          <Fig value={fmtPct(top.share)} sub={cash ? t('home:payments.figSubCash') : t('home:payments.figSubTop', { name: label(top.key, top.label) })} testId="payments-fig" />
          {credit && <Note testId="payments-note">{rich(t('home:payments.credit', { amount: fmtEur(credit.amount) }))}</Note>}
        </>
      ) : undefined}
    >
      {lines.length > 0 ? (
        <>
          <StackedBar parts={paymentStack(lines, label)} testId="payments-stack" />
          <table className="xh-tbl" data-testid="payments-table">
            <thead>
              <tr>
                <th>{t('home:payments.colMethod')}</th>
                <th>{t('home:payments.colAmount')}</th>
                <th>{t('home:payments.colShare')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => (
                <tr key={l.key}>
                  <td>
                    <span className="xh-nm">
                      <span className={`xh-sw bg${l.tone}`} aria-hidden="true" />
                      <span className="xh-trunc">
                        {label(l.key, l.label)}
                        {l.key === 'credit' && <span className="sub"> · {t('home:payments.notCollected')}</span>}
                      </span>
                    </span>
                  </td>
                  <td className="strong">{fmtEur(l.amount)}</td>
                  <td>{fmtPct(l.share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="xh-empty">{t('home:empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Προϊόντα (τα 5 πρώτα) ───────── */

export const ProductsTable: React.FC<{ rows: ProductRow[]; withDelta?: boolean; deltaTitle?: string; testId?: string }> = ({ rows, withDelta = false, deltaTitle, testId }) => {
  const { t } = useTranslation(['home']);
  const maxShare = Math.max(0, ...rows.map(r => r.share_pct));
  return (
    <table className="xh-tbl" data-testid={testId}>
      <thead>
        <tr>
          <th>{t('home:products.colProduct')}</th>
          <th>{t('home:products.colUnits')}</th>
          {withDelta && <th className="xh-opt">{deltaTitle}</th>}
          <th>{t('home:products.colRevenue')}</th>
          <th className="xh-opt">{t('home:products.colShare')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((p, i) => (
          <tr key={p.product_id ?? `${p.name}-${i}`}>
            <td>
              <span className="xh-nm"><span className="rk">{i + 1}</span><span className="xh-trunc" title={p.name}>{p.name}</span></span>
            </td>
            <td>{fmtNum(p.quantity)}</td>
            {withDelta && (
              <td className="xh-opt delta-n">
                {p.delta_quantity === null ? '—' : p.delta_quantity > 0 ? `+${fmtNum(p.delta_quantity)}` : p.delta_quantity < 0 ? `−${fmtNum(Math.abs(p.delta_quantity))}` : '0'}
              </td>
            )}
            <td className="strong">{fmtEur(p.revenue)}</td>
            <td className="xh-opt"><ShareCell share={p.share_pct} max={maxShare} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const ProductsSection: React.FC<{ top: ProductRow[]; onAll: () => void; locked: boolean }> = ({ top, onAll, locked }) => {
  const { t } = useTranslation(['home', 'analytics']);
  const rows = top.slice(0, HOME_TOP_PRODUCTS);
  const b0 = rows[0];
  return (
    <SheetSection
      id="home-products"
      title={t('home:products.title')}
      hint={t('home:products.hint', { count: HOME_TOP_PRODUCTS })}
      testId="section-products"
      aside={(
        <>
          {b0 && <Fig value={b0.name} sub={t('home:products.figSub', { units: fmtNum(b0.quantity), pct: fmtPct(b0.share_pct) })} testId="products-fig" />}
          <MoreLink label={t('home:products.all')} onClick={onAll} locked={locked} lockedTitle={t('analytics:storeAnalytics.premiumRequired')} testId="products-all" />
        </>
      )}
    >
      {rows.length > 0 ? <ProductsTable rows={rows} testId="products-table" /> : <p className="xh-empty">{t('home:products.empty')}</p>}
    </SheetSection>
  );
};

/* ───────── Προσωπικό ───────── */

export const StaffSection: React.FC<{
  data: HomeData;
  periodKey: PeriodKey;
  openSinceTime: string | null;
  onRegisterReports?: () => void;
}> = ({ data, periodKey, openSinceTime, onRegisterReports }) => {
  const { t } = useTranslation(['home']);
  const lines = staffLines(data.staff);
  const plat = platformTotals(data.channels);
  const rows = plat.orders > 0
    ? [...lines, { key: 'platforms', name: t('home:staff.platformsRow'), role: 'platforms' as const, orders: plat.orders, revenue: plat.revenue, muted: true }]
    : lines;
  const people = new Set(lines.map(l => l.name)).size;
  const totalRevenue = sum(rows.map(r => r.revenue));
  // Σύνολο μόνο όταν οι γραμμές «μοιράζουν» τις εισπράξεις (όπως το Ζ) — αλλιώς θα έδειχνε διπλομετρημένο ποσό.
  const showTotal = rows.length > 1 && Math.abs(totalRevenue - data.kpis.revenue.value) < 0.05;
  const roleLabel = (role: string): string => {
    if (role === 'platforms') return plat.names.join(' · ');
    return t(`home:staff.roles.${role}`);
  };
  return (
    <SheetSection
      id="home-staff"
      title={t('home:staff.title')}
      hint={periodKey === 'today' && openSinceTime ? t('home:staff.hintToday', { time: openSinceTime }) : t('home:staff.hintPeriod')}
      testId="section-staff"
      aside={(
        <>
          <Fig value={t('home:staff.people', { count: people })} sub={plat.orders > 0 ? t('home:staff.platforms', { count: plat.orders }) : undefined} testId="staff-fig" />
          {onRegisterReports && <MoreLink label={t('home:staff.zx')} onClick={onRegisterReports} testId="staff-zx" />}
        </>
      )}
    >
      {rows.length > 0 ? (
        <table className="xh-tbl" data-testid="staff-table">
          <thead>
            <tr>
              <th>{t('home:staff.colWho')}</th>
              <th>{t('home:staff.colOrders')}</th>
              <th>{t('home:staff.colRevenue')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className={r.muted ? 'muted' : undefined}>
                <td>
                  <span className="xh-nm">
                    <span className="xh-trunc" title={`${r.name} · ${roleLabel(r.role)}`}>
                      {r.name}
                      {roleLabel(r.role) && <span className="sub"> · {roleLabel(r.role)}</span>}
                    </span>
                  </span>
                </td>
                <td>{fmtNum(r.orders)}</td>
                <td className="strong">{fmtEur(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
          {showTotal && (
            <tfoot>
              <tr data-testid="staff-total">
                <td>{t('home:staff.total')}</td>
                <td>{fmtNum(sum(rows.map(r => r.orders)))}</td>
                <td>{fmtEur(totalRevenue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      ) : (
        <p className="xh-empty">{t('home:staff.empty')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Ακυρώσεις ───────── */

const CANCEL_LIST_MAX = 20;

/**
 * Παραγγελίες (ό,τι μετρά ο δείκτης) ΚΑΙ ακυρώσεις στα τραπέζια (ημερολόγιο: είδη «Διόρθωσης», ολόκληρα τραπέζια):
 * μία γραμμή ανά ομάδα που είχε κίνηση· «Αιτία» και «Ποιος ακύρωσε» ενωμένα σε δύο λίστες, όπως το δείγμα. Το
 * ποσοστό και η σύγκριση είναι των παραγγελιών (όπως ο δείκτης).
 */
export const CancellationsSection: React.FC<{ data: HomeData; periodKey: PeriodKey; dineIn?: boolean; onAll: () => void; locked: boolean }> = ({ data, periodKey, dineIn = false, onAll, locked }) => {
  const { t } = useTranslation(['home', 'analytics']);
  const c = data.cancellations;
  const orders = data.kpis.orders.value;
  const groups: Array<{ key: string; group: CancelGroup; testId: string }> = [
    { key: 'figOrders', group: c, testId: 'cancellations-fig-orders' },
    { key: 'figItems', group: c.items, testId: 'cancellations-fig-items' },
    { key: 'figTables', group: c.tables, testId: 'cancellations-fig-tables' },
  ].filter(g => g.group.count > 0);
  const total = sum(groups.map(g => g.group.count));
  // Μόνο παραγγελίες (π.χ. κατάστημα χωρίς τραπέζια): το νούμερο όπως ήταν· γραμμές με μονάδα μόνο όταν μπλέκονται ομάδες.
  const onlyOrders = groups.length === 1 && groups[0].key === 'figOrders';
  const rate = c.count + orders > 0 ? (c.count / (c.count + orders)) * 100 : 0;
  const prev = prevFromDelta(c.count, c.delta_pct);
  let sub: string | undefined;
  if (c.count > 0) {
    sub = [t('home:cancellations.figSub', { pct: fmtPct(rate) }), prev !== null ? compareShort(t, periodKey, prev) : null].filter(Boolean).join(' · ');
  } else if (total === 0) {
    sub = t('home:cancellations.none');
  }
  const all: CancelGroup[] = [c, c.items, c.tables];
  const reasons = mergeCounts(all.map(g => g.by_reason.map(r => ({ label: r.reason || t('home:cancellations.noReason'), count: r.count }))))
    .slice(0, CANCEL_LIST_MAX);
  const users = mergeCounts(all.map(g => g.by_user.map(r => ({ label: r.name, count: r.count })))).slice(0, CANCEL_LIST_MAX);
  const list = (rows: Array<{ label: string; count: number }>, head: string, testId: string) => (
    <div>
      <h3 className="xh-h3">{head}</h3>
      <table className="xh-tbl" data-testid={testId}>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.label}-${i}`}>
              <td><span className="xh-trunc" title={r.label}>{r.label}</span></td>
              <td className="strong">{fmtNum(r.count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <SheetSection
      id="home-cancellations"
      title={t('home:cancellations.title')}
      hint={t(dineIn || c.items.count > 0 || c.tables.count > 0 ? 'home:cancellations.hintTables' : 'home:cancellations.hint')}
      testId="section-cancellations"
      aside={(
        <>
          {total > 0 && !onlyOrders ? (
            <FigLines
              lines={groups.map(g => ({
                key: g.key,
                testId: g.testId,
                value: t(`home:cancellations.${g.key}`, { count: g.group.count, n: fmtNum(g.group.count), amount: fmtEur(g.group.amount) }),
              }))}
              sub={sub}
              testId="cancellations-fig"
            />
          ) : (
            <Fig value={onlyOrders ? `${fmtNum(c.count)} · ${fmtEur(c.amount)}` : '0'} sub={sub} testId="cancellations-fig" />
          )}
          <MoreLink label={t('home:cancellations.all')} onClick={onAll} locked={locked} lockedTitle={t('analytics:storeAnalytics.premiumRequired')} testId="cancellations-all" />
        </>
      )}
    >
      {total > 0 && (reasons.length > 0 || users.length > 0) ? (
        <div className="xh-two">
          {reasons.length > 0 && list(reasons, t('home:cancellations.byReason'), 'cancel-reasons')}
          {users.length > 0 && list(users, t('home:cancellations.byUser'), 'cancel-users')}
        </div>
      ) : (
        <p className="xh-empty">{total > 0 ? t('home:cancellations.noDetails') : t('home:cancellations.none')}</p>
      )}
    </SheetSection>
  );
};

/* ───────── Περισσότερες αναφορές ───────── */

export interface ReportLink {
  id: string;
  label: string;
  locked: boolean;
}

export const ReportsBlock: React.FC<{ items: ReportLink[]; onOpen: (id: string) => void }> = ({ items, onOpen }) => {
  const { t } = useTranslation(['home', 'analytics']);
  if (items.length === 0) return null;
  return (
    <SheetBlock className="xh-reports">
      <h2 className="xh-reports-title">{t('home:reports.title')}</h2>
      <ul>
        {items.map(it => (
          <li key={it.id}>
            <button
              type="button"
              className="xh-link"
              onClick={() => onOpen(it.id)}
              title={it.locked ? t('analytics:storeAnalytics.premiumRequired') : undefined}
              data-testid={`report-link-${it.id}`}
              data-locked={it.locked ? 'true' : undefined}
            >
              {it.label}
              {it.locked && <Lock className="xh-lock" aria-label={t('home:reports.lockedHint')} />}
            </button>
          </li>
        ))}
      </ul>
    </SheetBlock>
  );
};
